import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth/useAuth';
import { getLeadType } from '@/config/leadTypes';
import { Card } from '@/lib/ui/Card';
import { Eyebrow, MonoValue } from '@/lib/ui/Typography';
import type { Permission } from '@/lib/rbac/permissions';
import type { DashboardSummary } from '@/lib/api/types';
import { collectionLabel, collectionRoute, type TFn } from './collections';

/** One thing waiting on a person, with its count and where to go about it. */
export interface AttentionItem {
  id: string;
  tone: 'amber' | 'blue';
  label: string;
  count: number;
  /** A console route (react-router), or an in-page anchor when `anchor` is set. */
  to: string | null;
  anchor?: boolean;
}

/**
 * "What needs me?", derived from the summary the dashboard already fetched — the panel
 * makes no request of its own (one `GET /admin/dashboard`, never a fan-out per inbox).
 *
 * - Dead outbox messages: an e-mail that never left, or a revalidation that never ran, so a
 *   record reads "published" here while the site still serves the old version. `outbox` is
 *   `null` for callers without `settings:manage`, and then the row is simply not offered.
 * - Publish-blocked drafts, per collection: the same check as the list's `blocker_count`, so
 *   the link lands on the drafts that list flags.
 * - Unseen leads per inbox: nobody has opened them yet (`?unseen=true` is the same set).
 */
export function attentionItems(
  d: DashboardSummary,
  { can, salesOnly, t }: { can: (p: Permission) => boolean; salesOnly: boolean; t: TFn },
): AttentionItem[] {
  const items: AttentionItem[] = [];

  const dead = d.outbox?.dead ?? 0;
  if (d.outbox && dead > 0 && can('settings:manage')) {
    items.push({
      id: 'outbox-dead',
      tone: 'amber',
      label: t('attention.deadOutbox'),
      count: dead,
      to: '#outbox-queue',
      anchor: true,
    });
  }

  if (!salesOnly) {
    for (const [col, n] of Object.entries(d.blocked_drafts ?? {})) {
      if (typeof n !== 'number' || n <= 0) continue;
      const route = collectionRoute(col);
      items.push({
        id: `blocked-${col}`,
        tone: 'amber',
        label: t('attention.blockedDrafts', { collection: collectionLabel(col, t) }),
        count: n,
        to: route ? `${route}?status=draft` : null,
      });
    }
  }

  for (const [type, lead] of Object.entries(d.leads ?? {})) {
    const unseen = lead && typeof lead === 'object' ? (lead.unseen ?? 0) : 0;
    if (unseen <= 0) continue;
    const cfg = getLeadType(type);
    if (cfg && !can(cfg.readPermission)) continue;
    items.push({
      id: `unseen-${type}`,
      tone: 'blue',
      label: t('attention.unseenLeads', {
        inbox: cfg ? t(`nav.${cfg.navKey}`) : type.replace(/[_-]/g, ' '),
      }),
      count: unseen,
      to: cfg ? `/leads/${type}?unseen=true` : null,
    });
  }

  return items;
}

/**
 * The dashboard's "Needs attention" panel. When nothing is waiting it says so in one line
 * rather than vanishing: "all clear" is an answer, and a section that disappears reads the
 * same as one that failed to load.
 */
export function AttentionPanel({ summary, salesOnly }: { summary: DashboardSummary; salesOnly: boolean }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const items = attentionItems(summary, { can, salesOnly, t });

  return (
    <section aria-label={t('attention.title')}>
      <Eyebrow>{t('attention.title')}</Eyebrow>
      {items.length === 0 ? (
        <p className="mt-sm text-small text-ink-mid">{t('attention.allClear')}</p>
      ) : (
        <Card className="mt-sm p-0">
          <ul className="divide-y divide-hairline">
            {items.map((item) => {
              const linkClass =
                'rounded-sm text-small text-accent-blue underline-offset-4 hover:underline focus-visible:shadow-focus';
              return (
                <li key={item.id} className="flex flex-wrap items-center justify-between gap-md px-lg py-sm">
                  <span className="flex items-center gap-sm text-small text-ink-deep">
                    <span
                      aria-hidden
                      className={
                        item.tone === 'amber'
                          ? 'h-[8px] w-[8px] shrink-0 rounded-pill bg-signal-amber'
                          : 'h-[8px] w-[8px] shrink-0 rounded-pill bg-accent-blue'
                      }
                    />
                    {item.label}
                    <MonoValue className="text-ink-deep">{item.count}</MonoValue>
                  </span>
                  {item.to &&
                    (item.anchor ? (
                      <a href={item.to} className={linkClass}>
                        {t('attention.review')}
                        <span className="sr-only">: {item.label}</span>
                      </a>
                    ) : (
                      <Link to={item.to} className={linkClass}>
                        {t('attention.review')}
                        <span className="sr-only">: {item.label}</span>
                      </Link>
                    ))}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </section>
  );
}
